import { useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment/moment";
import {
  IconDotsVertical,
  IconFileFilled,
  IconFolderFilled,
  IconPlus,
  IconSignature,
} from "@tabler/icons-react";
import { Button as BaseButton } from "@base-ui/react/button";
import DropdownMenu from "../UI/DropdownMenu";
// @ts-expect-error pure js
import DocumentsFormUpload from "./DocumentsFormUpload";
// @ts-expect-error pure js
import ElectronicSigning from "../ElectronicSigning/ElectronicSigning";
// @ts-expect-error pure js
import { ProfileStatus } from "../../models/profile";
// @ts-expect-error pure js
import { useModal } from "../../hooks/use-modal.jsx";
// @ts-expect-error pure js
import { useConfirm } from "../../hooks/use-confirm.jsx";
// @ts-expect-error pure js
import ToggleSwitch from "../UI/ToggleSwitch";
// @ts-expect-error pure js
import { FileStatus } from "../../models/documents";
import ContractStatusIndicator from "../ElectronicSigning/ContractStatusIndicator";
import styles from "./DocumentCard.module.css";
import Status from "../UI/Status";
import Card from "../UI/Card";
import Text from "../UI/Text";
// @ts-expect-error pure js
import SendMailForm from "./SendMailForm";
import DocumentDownloadLink from "../UI/DocumentDownloadLink";
// @ts-expect-error pure js
import DocumentThumbnail from "../UI/DocumentThumbnail";
// @ts-expect-error pure js
import { SignatureContratStatus } from "../../models/signatureElectronique";
// @ts-expect-error pure js
import Oodrive from "../Oodrive/Oodrive";
// @ts-expect-error pure js
import SendRecommandeForm from "../Laposte/SendRecommandeForm";
// @ts-expect-error pure js
import SuiviRecommande from "../Laposte/SuiviRecommande";
import LaposteStatusIndicator from "../Laposte/LaposteStatusIndicator";
// @ts-expect-error pure js
import useAuthenticatedAxios from "../../hooks/use-authenticated-axios";
// @ts-expect-error pure js
import useAuth from "../../hooks/use-auth";
import { useToast } from "../UI/Toast/useToast";
import Button from "../UI/Button";
import type { AxiosInstance } from "axios";
// @ts-expect-error pure js
import { FileTag } from "../../utils/constants/documents";
import useDocumentDownload from "../../hooks/use-document-download";
import useCanWrite from "../../hooks/use-can-write";
import Tooltip from "../UI/Tooltip";
import { READ_ONLY_MESSAGE } from "../../utils/permissions";
import type { Tenant } from "../../models/tenant";
import type { Property } from "../../models/property";
import type { PropertyFolder } from "../../models/propertyFolder";
import {
  getExpirationStatus,
  getGroupExpirationStatus,
  type Signataire,
  type DocumentFile,
  type ExpirationStatus,
} from "../../models/document";
import {
  DocumentListCell,
  DocumentListName,
  DocumentListRow,
  DocumentListSelectCell,
} from "./DocumentListRow";

// Statuts Oodrive considérés comme signés (mêmes valeurs que côté back, cf. relance_signature_electronique)
const SIGNED_SIGNATURE_STATUSES = ["SIGNED", "COMPLETED"];

function hasSigned(signataire: Signataire) {
  return SIGNED_SIGNATURE_STATUSES.includes(
    String(signataire.status ?? "").toUpperCase(),
  );
}

function signButtonLabel(signataire: Signataire, authMail?: string) {
  const name = [signataire.prenom, signataire.nom].filter(Boolean).join(" ");
  return signataire.mail === authMail || !name
    ? "Signer le document"
    : `Signer — ${name}`;
}

interface DocumentCardProps {
  files: DocumentFile[];
  titre: string;
  tag?: string;
  tenant?: Tenant;
  isBailleur?: boolean;
  deleteDocumentHandler?: (fileId: number) => void;
  property?: Property;
  propertyFolder?: PropertyFolder;
  isLocataire?: boolean;
  sendToElectronicSigning?: (fileId: number) => Promise<unknown>;
  updateFile?: (
    fileId: number,
    values: Record<string, unknown>,
  ) => Promise<unknown>;
  onDetailFiles?: (tag: string) => void;
  allowEdit?: boolean;
  cancelElectronicSigning?: (fileId: number) => Promise<unknown>;
  relanceSignataire?: (
    fileId: number,
    signataireId: number,
  ) => Promise<unknown>;
  sendToLaPoste?: (
    fileId: number,
    values: Record<string, unknown>,
  ) => Promise<unknown>;
  profileId?: number | null;
  /** « row » : une ligne de la vue liste au lieu d'une carte (fichier seul uniquement). */
  layout?: "card" | "row";
  selected?: boolean;
  /** Absent : la ligne n'est pas sélectionnable. */
  onSelectedChange?: (fileId: number, selected: boolean) => void;
  /** Fichier d'un dossier déplié en vue liste. */
  child?: boolean;
  last?: boolean;
  showShareColumn?: boolean;
}

function ExpirationBadge({ status }: { status: ExpirationStatus }) {
  if (status === "expired") {
    return <Status color="danger" text="Expiré" />;
  }
  if (status === "expiring") {
    return <Status color="warning" text="Expire bientôt" />;
  }
  return null;
}

export default function DocumentCard({
  files,
  titre,
  tag,
  tenant,
  isBailleur,
  deleteDocumentHandler,
  property,
  propertyFolder,
  isLocataire,
  sendToElectronicSigning,
  updateFile,
  onDetailFiles,
  allowEdit,
  cancelElectronicSigning,
  relanceSignataire,
  sendToLaPoste,
  profileId,
  layout = "card",
  selected = false,
  onSelectedChange,
  child,
  last,
  showShareColumn,
}: DocumentCardProps) {
  const [DocumentModal, closeDocumentModal, openDocumentModal] = useModal();
  const [SendMailModal, closeSendMailModal, openSendMailModal] = useModal();
  const [SendLetterModal, closeSendLetterModal, openSendLetterModal] =
    useModal();
  const [OpenSuiviLaposteModal, , openOpenSuiviLaposteModal] = useModal();
  const [OodriveModal, closeOodriveModal, openOodriveModal] = useModal();
  const [
    SignatureElectroniqueModal,
    closeSignatureElectroniqueModal,
    openSignatureElectroniqueModal,
  ] = useModal();
  const [fileToEdit, setFileToEdit] = useState<DocumentFile | null>(null);
  const [fileLaposteSuivi, setFileLaposteSuivi] = useState<DocumentFile | null>(
    null,
  );
  const [Confirm, openConfirm] = useConfirm();
  const axios: AxiosInstance = useAuthenticatedAxios();
  const canWrite = useCanWrite(profileId);
  const { auth } = useAuth();
  const toast = useToast();

  const file = files.length === 1 ? files[0] : undefined;
  const isFileSentForSigning = !!file && !!file.signature_electronique_status;
  const isFileWaitingForSignature =
    isFileSentForSigning &&
    (file!.signature_electronique_status ===
      SignatureContratStatus.Opened.status ||
      file!.signature_electronique_status ===
        SignatureContratStatus.Open.status);
  // Un bouton par signataire « de mon côté » encore en attente (ex. profil bailleur à deux
  // personnes sur un même compte : chacune doit voir son bouton). On identifie mon côté par
  // le mail du compte quand il correspond, sinon en séparant signataires locataires et
  // bailleurs via les profils du locataire. Le back reste seul juge des liens accessibles.
  const signataires = file?.signataires ?? [];
  const tenantSideMails = new Set(
    (tenant?.profiles ?? [])
      .flatMap((profile) => [
        profile.mail,
        ...(profile.garantors ?? []).map((garant) => garant.mail),
        ...(profile.legal_representatives ?? []).map((rep) => rep.mail),
      ])
      .filter(Boolean),
  );
  const signatairesOfMyMail = signataires.filter(
    (signataire) => signataire.mail === auth.mail,
  );
  let mySideSignataires: Signataire[] = [];
  if (signatairesOfMyMail.length > 0) {
    mySideSignataires = signatairesOfMyMail;
  } else if (isLocataire) {
    mySideSignataires = signataires.filter(
      (signataire) => signataire.mail && tenantSideMails.has(signataire.mail),
    );
  } else if (isBailleur && tenant?.profiles?.length) {
    mySideSignataires = signataires.filter(
      (signataire) => !signataire.mail || !tenantSideMails.has(signataire.mail),
    );
  }
  const signatairesToSign = isFileWaitingForSignature
    ? mySideSignataires.filter((signataire) => !hasSigned(signataire))
    : [];
  // Personne identifié ? Sinon bouton générique : le back tranchera au clic.
  const showGenericSignButton =
    isFileWaitingForSignature && mySideSignataires.length === 0;
  const isFileSigned =
    isFileSentForSigning &&
    file!.signature_electronique_status ===
      SignatureContratStatus.Archived.status;
  const isSignatureCanceled =
    isFileSentForSigning &&
    file!.signature_electronique_status ===
      SignatureContratStatus.Canceled.status;
  const isFileSentWithLaposte = !!file && file.id_laposte !== null;
  const isInheritedFromFolder =
    !!property && !!file && !!file.id_property_folder;

  const navigate = useNavigate();
  const { downloadHandler: downloadCurrentFile } = useDocumentDownload({
    documentId: file?.id,
    file: file,
    isSigned: isFileSigned,
    save: true,
  });
  // Vue liste : un clic sur la ligne ouvre l'aperçu, comme le clic sur la vignette d'une carte.
  const { downloadHandler: previewCurrentFile, overlay: previewOverlay } =
    useDocumentDownload({
      documentId: file?.id,
      file: file,
      isSigned: isFileSigned,
    });

  async function sendRecommandeHandler(
    id_file: number,
    values: Record<string, unknown>,
  ) {
    await sendToLaPoste?.(id_file, values);
    closeSendLetterModal();
  }

  async function downloadSignatureProof(fileId: number) {
    try {
      const response = await axios.post(`/files/${fileId}/oodrive-proof`);
      window.open(response.data, "_blank");
    } catch (error) {
      console.error(error);
    }
  }

  async function openMySignatureLink(fileId: number, signataire?: Signataire) {
    // Onglet ouvert de façon synchrone pendant le clic (sinon le navigateur le bloque comme
    // popup), puis redirigé une fois le lien récupéré.
    const signTab = window.open("", "_blank");
    try {
      const response = await axios.get(`/oodrive/${fileId}/my-signature-links`);
      const myLinks: Signataire[] = response.data;
      const pending = myLinks.filter((link) => !hasSigned(link));
      // Bouton nominatif : le lien de ce signataire précis. Bouton générique : d'abord le
      // signataire du compte connecté, sinon le prochain qui n'a pas encore signé.
      const target = signataire
        ? myLinks.find((link) => link.id === signataire.id)
        : pending.find((link) => link.mail === auth.mail) ??
          pending[0] ??
          myLinks[0];
      if (!target?.link) {
        signTab?.close();
        toast.info({
          title: "Aucun lien de signature disponible pour votre compte",
          description:
            "Vous n'êtes pas signataire de ce document, ou le lien n'a pas pu être récupéré.",
        });
        return;
      }
      if (signTab) {
        signTab.location.href = target.link;
      } else {
        window.location.href = target.link;
      }
    } catch (error) {
      signTab?.close();
      console.error(error);
      toast.error({ title: "Impossible de récupérer le lien de signature" });
    }
  }

  // Un document hérité de l'immeuble se gère depuis l'immeuble : l'éditer d'ici le
  // rattacherait au bien.
  const renderInheritedMenu = (targetFile: DocumentFile) => (
    <DropdownMenu icon={IconDotsVertical} label="Actions sur le document">
      <DropdownMenu.Item label="Télécharger" onClick={downloadCurrentFile} />
      <DropdownMenu.Item
        label="Gérer depuis l'immeuble"
        onClick={() =>
          navigate(
            `/bailleur/properties/folder/${targetFile.id_property_folder}?tab-prop-show=documents`,
          )
        }
      />
    </DropdownMenu>
  );

  const renderDropdownMenu = (targetFile: DocumentFile) => (
    <DropdownMenu icon={IconDotsVertical} label="Actions sur le document">
      <DropdownMenu.Item label="Télécharger" onClick={downloadCurrentFile} />
      {updateFile && (
        <DropdownMenu.Item
          label="Editer"
          requiresWrite
          profileId={profileId}
          onClick={() => {
            setFileToEdit(targetFile);
            openDocumentModal();
          }}
        />
      )}
      {sendToElectronicSigning &&
        (!isFileSentForSigning || isSignatureCanceled) && (
          <DropdownMenu.Item
            label="Signer électroniquement"
            requiresWrite
            profileId={profileId}
            onClick={() => {
              setFileToEdit(targetFile);
              openSignatureElectroniqueModal();
            }}
          />
        )}
      {isFileSentForSigning && !isSignatureCanceled && isBailleur && (
        <DropdownMenu.Item
          label="Gérer signature électronique"
          requiresWrite
          profileId={profileId}
          onClick={() => {
            setFileToEdit(targetFile);
            openOodriveModal();
          }}
        />
      )}
      {isFileSigned && (
        <DropdownMenu.Item
          label="Télécharger la preuve de signature"
          onClick={() => downloadSignatureProof(targetFile.id)}
        />
      )}
      {tenant && isBailleur && (
        <DropdownMenu.Item
          label="Envoyer par mail"
          requiresWrite
          profileId={profileId}
          onClick={() => {
            setFileToEdit(targetFile);
            openSendMailModal();
          }}
        />
      )}
      {tenant && isBailleur && !isFileSentWithLaposte && (
        <DropdownMenu.Item
          label="Envoyer par lettre recommandée"
          requiresWrite
          profileId={profileId}
          onClick={() => {
            setFileToEdit(targetFile);
            openSendLetterModal();
          }}
        />
      )}
      {tenant && isBailleur && isFileSentWithLaposte && (
        <DropdownMenu.Item
          label="Suivre ma lettre recommandée"
          onClick={() => {
            setFileLaposteSuivi(targetFile);
            openOpenSuiviLaposteModal();
          }}
        />
      )}
      {deleteDocumentHandler && (
        <DropdownMenu.Item
          label="Supprimer"
          variant="danger"
          requiresWrite
          profileId={profileId}
          onClick={() => openConfirm(targetFile.id)}
        />
      )}
    </DropdownMenu>
  );

  const renderModals = () => (
    <>
      <Confirm
        onAnswer={(ok: boolean, fileId: number) => {
          if (ok) {
            deleteDocumentHandler?.(fileId);
          }
        }}
      >
        <Text>
          Voulez-vous vraiment supprimer ce fichier ? Vous ne pourrez pas
          revenir en arrière.
        </Text>
      </Confirm>
      <DocumentModal onClose={() => setFileToEdit(null)}>
        <DocumentsFormUpload
          property={property}
          propertyFolder={propertyFolder}
          tenant={tenant}
          onSubmitted={closeDocumentModal}
          isBailleur={isBailleur}
          isLocataire={isLocataire}
          file={fileToEdit}
          typeDocument={tag}
        />
      </DocumentModal>
      <SendMailModal onClose={() => setFileToEdit(null)} size="small">
        <SendMailForm
          tenant={tenant}
          file={fileToEdit}
          onSubmitted={closeSendMailModal}
        />
      </SendMailModal>
      <SendLetterModal onClose={() => setFileToEdit(null)} size="small">
        <SendRecommandeForm
          tenant={tenant}
          file={fileToEdit}
          onSend={sendRecommandeHandler}
        />
      </SendLetterModal>
      <OpenSuiviLaposteModal
        onClose={() => setFileLaposteSuivi(null)}
        size="intermediate"
      >
        <SuiviRecommande file={fileLaposteSuivi} />
      </OpenSuiviLaposteModal>
      <OodriveModal>
        <Oodrive
          file={fileToEdit}
          onCancelSigning={async (fileId: number) => {
            try {
              await cancelElectronicSigning?.(fileId);
              closeOodriveModal();
            } catch (e) {
              console.error(e);
            }
          }}
          onRelance={async (fileId: number, signataireId: number) => {
            try {
              await relanceSignataire?.(fileId, signataireId);
            } catch (e) {
              console.error(e);
            }
          }}
        />
      </OodriveModal>
      <SignatureElectroniqueModal>
        <ElectronicSigning
          file={fileToEdit}
          propertyId={tenant?.id_bien}
          tenantProfiles={tenant?.profiles?.filter(
            (profile) =>
              profile.garantor_of === null &&
              profile.status === ProfileStatus.Actif.status,
          )}
          onSendToElectronicSigning={async () => {
            if (!fileToEdit) return;
            await sendToElectronicSigning?.(fileToEdit.id);
            closeSignatureElectroniqueModal();
          }}
        />
      </SignatureElectroniqueModal>
    </>
  );

  if (file) {
    let displayTitle = titre;
    if (file.tag === FileTag.QUITTANCE) {
      displayTitle = `Quittance ${moment(file.meta!.date_quittance).format(
        "MMM YYYY",
      )}`;
    }

    const statuses = (
      <>
        <ContractStatusIndicator file={file} />
        <LaposteStatusIndicator file={file} />
        {isInheritedFromFolder && !isLocataire && (
          <Status color="info" text="Hérité de l'immeuble" />
        )}
        {file.status === FileStatus.aValider.status && (
          <Status color="warning" text="À valider" />
        )}
        <ExpirationBadge status={getExpirationStatus(file.valid_until)} />
      </>
    );
    const signButtons = (
      <>
        {signatairesToSign.map((signataire) => (
          <Button
            key={signataire.id}
            variant="secondary"
            icon={IconSignature}
            onClick={() => openMySignatureLink(file.id, signataire)}
          >
            {signButtonLabel(signataire, auth.mail)}
          </Button>
        ))}
        {showGenericSignButton && (
          <Button
            variant="secondary"
            icon={IconSignature}
            onClick={() => openMySignatureLink(file.id)}
          >
            Signer le document
          </Button>
        )}
      </>
    );
    const canToggleShare = allowEdit && !tenant && !isInheritedFromFolder;
    const dateAdded = file.date_add
      ? moment(file.date_add).format("DD/MM/YYYY")
      : "";

    if (layout === "row") {
      return (
        <>
          {renderModals()}
          {previewOverlay}
          <DocumentListRow
            selected={selected}
            child={child}
            last={last}
            onClick={previewCurrentFile}
          >
            <DocumentListSelectCell
              checked={selected}
              label={`Sélectionner ${displayTitle}`}
              onChange={
                onSelectedChange
                  ? (checked) => onSelectedChange(file.id, checked)
                  : undefined
              }
              disabledReason={
                isInheritedFromFolder && onSelectedChange
                  ? "Ce document se gère depuis l'immeuble"
                  : undefined
              }
            />
            <DocumentListName
              icon={IconFileFilled}
              title={displayTitle}
              mobileSubtitle={[
                dateAdded && `Ajouté le ${dateAdded}`,
                showShareColumn && file.shared && "Partagé au locataire",
              ]
                .filter(Boolean)
                .join(" · ")}
            />
            <DocumentListCell column="status" className={styles.rowStatuses}>
              {statuses}
              {signButtons}
            </DocumentListCell>
            <DocumentListCell column="share">
              {showShareColumn &&
                (canToggleShare ? (
                  <div className={styles.rowShare}>
                    <ToggleSwitch
                      status={file.shared}
                      requiresWrite
                      profileId={profileId}
                      onToggle={() => {
                        updateFile?.(file.id, { shared: !file.shared });
                      }}
                    />
                    <Text as="span" variant="deci">
                      {file.shared ? "Oui" : "Non"}
                    </Text>
                  </div>
                ) : (
                  <Text variant="deci">{file.shared ? "Oui" : "Non"}</Text>
                ))}
            </DocumentListCell>
            <DocumentListCell column="date">
              <Text variant="deci">{dateAdded}</Text>
            </DocumentListCell>
            <DocumentListCell column="actions">
              {isInheritedFromFolder
                ? renderInheritedMenu(file)
                : renderDropdownMenu(file)}
            </DocumentListCell>
          </DocumentListRow>
        </>
      );
    }

    return (
      <>
        {renderModals()}
        <Card className={styles.documentCard} padding="sm">
          <div className={styles.cardHeader}>
            <IconFileFilled size={20} />
            <Text
              variant="deca"
              weight="bold"
              className={styles.cardTitle}
              title={displayTitle}
            >
              {displayTitle}
            </Text>
            {isInheritedFromFolder
              ? renderInheritedMenu(file)
              : renderDropdownMenu(file)}
          </div>

          <DocumentDownloadLink
            documentId={file.id}
            file={file}
            isSigned={isFileSigned}
            className={styles.imagePreview}
          >
            <DocumentThumbnail
              documentId={file.id}
              file={file}
              isSigned={isFileSigned}
              style={{ height: "125px", aspectRatio: "unset" }}
            />
            <div className={styles.statuses}>{statuses}</div>
          </DocumentDownloadLink>

          {signButtons}

          {canToggleShare && (
            <div className={styles.shareRow}>
              <Text variant="deci">Partagé au locataire</Text>
              <ToggleSwitch
                status={file.shared}
                requiresWrite
                profileId={profileId}
                onToggle={() => {
                  updateFile?.(file.id, { shared: !file.shared });
                }}
              />
            </div>
          )}

          {file.date_add && (
            <Text variant="deci">
              {`Créé le ${moment(file.date_add).format("D MMM YYYY")}`}
            </Text>
          )}
        </Card>
      </>
    );
  }

  if (files.length > 1) {
    const groupExpiration = getGroupExpirationStatus(files);
    return (
      <>
        {renderModals()}
        <Card padding="sm" onClick={() => onDetailFiles?.(files[0].tag)}>
          <div className={styles.cardHeader}>
            <IconFolderFilled size={20} />
            <Text variant="deca" weight="bold" className={styles.cardTitle}>
              {titre}
            </Text>
          </div>
          <Text variant="deci">{files.length} documents</Text>
          {groupExpiration && (
            <div className={styles.groupStatuses}>
              <ExpirationBadge status={groupExpiration} />
            </div>
          )}
        </Card>
      </>
    );
  }

  return (
    <>
      {renderModals()}
      <div>
        <Text weight="medium">{titre}</Text>
        {canWrite ? (
          <BaseButton
            className={styles.emptyCard}
            onClick={() => openDocumentModal()}
          >
            <div className={styles.emptyIconBox}>
              <IconPlus size={24} />
            </div>
            <Text as="span" weight="bold">
              Ajouter le document
            </Text>
            <Text as="span" variant="deci">
              Cliquez pour renseigner les informations
            </Text>
          </BaseButton>
        ) : (
          <Tooltip content={READ_ONLY_MESSAGE} direction="top">
            <BaseButton className={styles.emptyCard} disabled>
              <div className={styles.emptyIconBox}>
                <IconPlus size={24} />
              </div>
              <Text as="span" weight="bold">
                Ajouter le document
              </Text>
              <Text as="span" variant="deci">
                Cliquez pour renseigner les informations
              </Text>
            </BaseButton>
          </Tooltip>
        )}
      </div>
    </>
  );
}
